import { Track } from "@/src/lib/tracking";
import {
  CheckoutUiMode,
  type MemberProduct,
  type OfferDetail,
  PayChannel,
  type PaymentCheckResponseData,
  type PaymentContext,
  type WalletSign,
} from "@api/memberApi";
import { type ApiErrorBody, getApiErrorBody } from "@features/api/osp/errors";
import { wrapperResult } from "@api/osp";

import { useContext } from "react";
import { getDeviceType, getGaInfoForReporting } from "utils";
import { mergeAdjustAdidIntoHeaders } from "@/src/lib/bridge/adjustRequestHeaders";
import { OspApiClientContext } from "@features/api/client/OspApiClientProvider";
import { buildConsentPayload } from "@utils/member/checkoutConsent";
import { saveCheckoutStash } from "@utils/member/checkoutSessionStash";
import { I18nUtil } from "@utils/i18n";
import { ROUTER_PATH } from "@config/router";

/** `/payment/check` 的调用参数（EDG-552：渠道必传，不再默认 STRIPE）。 */
export interface PaymentCheckOptions {
  /** 必传：渠道顺序只来自后端 payment_configs，调用方用 `pickCheckoutChannels` 取得 */
  payChannel: PayChannel;
  /**
   * 支付完成后回跳的**站内路径**（默认 `ROUTER_PATH.MEMBER`）。
   * 消费 `?payment=success` 的只有 `/member`（`Member.tsx`）：回跳到别的页面，用户付完落在一个没有 handler 的页面上，
   * 不主动确认、不弹成功弹窗、不刷会员缓存、GA4 `purchase` 也不会上报（beta 2026-10-14 G0：从任务页发起，回跳任务页）。
   * 所以默认固定回 /member，不再取发起时的页面。
   */
  returnPath?: string;
  returnQuery?: Record<string, string>;
  /** 实际结算的产品：订阅 / 试用带 consent，终身不带（`buildConsentPayload`） */
  product?: MemberProduct;
}

export type PaymentCheckError = ApiErrorBody;

export interface PaymentCheckResult {
  /** 收银台链接：优先 `links[rel=checkout]`（Airwallex 托管页），否则 `links[0]`（Stripe / PayPal） */
  data: string | null;
  /** 渠道会话 id；存在时已写入 `checkoutSessionStash`，回跳后用于 `/payment/confirm` */
  sessionId?: string;
  error: PaymentCheckError | null;
}

const CHECKOUT_LINK_REL = "checkout";

function pickCheckoutLink(offer?: OfferDetail): string | undefined {
  const links = offer?.links ?? [];
  return (
    links.find((link) => link.rel === CHECKOUT_LINK_REL)?.href ?? links[0]?.href
  );
}

/**
 * 回跳 URL 追加 `&channel={payChannel}`，供 `Member.tsx` 回跳后按渠道区分 GTM orderId 与是否主动确认。
 * `locale` 按 BCP-47（`en-US` / `zh-TW`）传：PayPal 直接使用，Airwallex 由后端按语言前缀映射。
 */
function buildPaymentContext(
  planId: string,
  options: PaymentCheckOptions,
): PaymentContext {
  const returnPath = options.returnPath ?? ROUTER_PATH.MEMBER;
  const extraQuery = options.returnQuery
    ? `&${new URLSearchParams(options.returnQuery).toString()}`
    : "";
  const base = `${window.location.origin}${returnPath}`;
  const query = `planId=${planId}&channel=${options.payChannel}${extraQuery}`;
  return {
    return_url: `${base}?payment=success&${query}`,
    cancel_url: `${base}?payment=cancel&${query}`,
    locale: I18nUtil.getBackgroundLanguageKey(I18nUtil.getCurrentLocale()),
  };
}

export const usePayment = () => {
  const { memberApi } = useContext(OspApiClientContext);

  const handlePaymentCheck = async (
    planId: string,
    options: PaymentCheckOptions,
  ): Promise<PaymentCheckResult> => {
    try {
      if (!memberApi?.payment?.paymentCheck) {
        throw new Error("Payment check client is not available");
      }
      const { payChannel, product } = options;
      const gaInfo = await getGaInfoForReporting();
      const headers = mergeAdjustAdidIntoHeaders({
        "ga-instance-id": gaInfo.gaInstanceId || "",
        "ga-client-id": gaInfo.clientId || "",
        "ga-session-id": gaInfo.sessionId || "",
        "os-device-type": getDeviceType(),
      });
      // 经 wrapperResult 解包：HTTP 200 但 code≠0 与非 2xx 都会抛出，错误码才能被映射（AC-23）
      const data = await wrapperResult<PaymentCheckResponseData>(
        memberApi.payment.paymentCheck(
          {
            product_id: planId,
            pay_channel: payChannel,
            checkout_ui_mode: CheckoutUiMode.HOSTED,
            consent: product ? buildConsentPayload(product) : undefined,
            payment_context: buildPaymentContext(planId, options),
          },
          {
            headers,
          },
        ),
      );
      const offer = data?.offer_details?.[0];
      const checkoutUrl = pickCheckoutLink(offer);
      const sessionId = offer?.session_id;
      if (checkoutUrl && sessionId) {
        saveCheckoutStash({
          kind: "checkout",
          sessionId,
          planId,
          channel: payChannel,
          createdAt: Date.now(),
        });
      }
      return { data: checkoutUrl ?? null, sessionId, error: null };
    } catch (error: unknown) {
      const { code, msg } = getApiErrorBody(error);
      Track.log("error", "getPaymentCheck error", "getPaymentCheck error", {
        arg1: String(error),
        arg2: String(planId),
        code: String(code ?? ""),
      });
      return { data: null, error: { code, msg } };
    }
  };

  const handleWalletSign = async ({
    planId,
    eoa_address,
    chain_id,
    token_address,
    sign_type,
    type,
  }: {
    planId?: string;
    eoa_address: string;
    chain_id: number;
    token_address?: string;
    sign_type?: string;
    type?: string;
  }) => {
    try {
      if (!memberApi?.wallet?.sign) {
        throw new Error("Wallet sign client is not available");
      }
      const gaInfo = await getGaInfoForReporting();
      const headers = mergeAdjustAdidIntoHeaders({
        "ga-instance-id": gaInfo.gaInstanceId || "",
        "ga-client-id": gaInfo.clientId || "",
        "ga-session-id": gaInfo.sessionId || "",
        "os-device-type": getDeviceType(),
      });
      const res = await wrapperResult<WalletSign>(
        memberApi.wallet.sign(
          {
            eoa_address,
            product_id: planId,
            chain_id,
            token_address,
            type: type || "EDGEN_SUB",
            sign_type: sign_type || "PAYMENT",
          },
          {
            headers,
          },
        ),
      );
      return {
        data: res,
        error: null,
      };
    } catch (error: unknown) {
      const err = error as {
        data?: { code?: string; msg?: string; data?: unknown };
      };
      Track.log("error", "walletSign error", "walletSign error", {
        arg1: String(err?.data),
        arg2: String(err?.data?.code),
        arg3: String(planId),
      });
      return {
        data: null,
        error: {
          code: err.data?.code,
          msg: err.data?.msg,
          data: err.data?.data,
        },
      };
    }
  };

  return {
    handleWalletSign,
    handlePaymentCheck,
  };
};
